import { Vec2 } from "utils/math/vec2";
import { Webgl2dRenderer } from "../../../webgl2d-renderer";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";
import { CropSelectionComponent } from "./crop-selection-component";
import { CropUnitComponent } from "./crop-unit-component";
import type { Nullable } from "models-webgl2";
import { ImageAdjustRectComponent } from "../image-adjust-rect-component";
import { ImageRectComponent } from "../image-rect-component";
import { Mat3 } from "utils/math/mat3";

export class CropSelectionLeftComponent extends Webgl2Component {
  private _tm3: Mat3;
  private _cropBottomRight: Vec2;

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  constructor(
    private readonly _renderer: Webgl2dRenderer,
    private readonly _cropSelectionComponent: CropSelectionComponent,
  ) {
    super();

    const leftUnit = new CropUnitComponent(new Vec2(4, 16));
    leftUnit.style.left = "50%";
    leftUnit.style.translateX = "-50%";
    leftUnit.style.pointerEvents = "none";
    this.appendChild(leftUnit);

    this.transform.setSize(16, 16);
  }

  public onMouseDown() {
    if (!this._selectedComponent) return;

    const selectedBottomRight = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._selectedComponent,
      Vec2.one(),
    );
    const cropBottomRight = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._cropSelectionComponent,
      Vec2.one(),
    );
    this._tm3 = Mat3.identity()
      .flipXY()
      .rotate(-this._selectedComponent.transform.rads)
      .translate(selectedBottomRight.clone().negate());
    this._cropBottomRight = cropBottomRight.transformMat3(this._tm3.mat);
  }

  public onMouseMove(e: MouseEvent | TouchEvent) {
    const currentMousePos = this._renderer.camera
      .getGlobalMousePixelPosition(e)
      .transformMat3(this._tm3.mat)
      .clampX(this._cropBottomRight.x, Infinity);

    const newSize = new Vec2(
      currentMousePos.x - this._cropBottomRight.x,
      this._cropSelectionComponent.transform.size.y,
    );

    this._cropSelectionComponent.transform.calcDimensions({
      size: newSize,
      position: new Vec2(
        (this._cropBottomRight.x + currentMousePos.x) / 2,
        this._cropBottomRight.y + newSize.y / 2,
      ).transformMat3(this._tm3.clone().invert().mat),
    });
    this._renderer.render();
  }

  public getRenderData() {
    const material = Webgl2.materials.transparent;
    return { material };
  }
}

export default CropSelectionLeftComponent;
